import useUserStore from 'store/user.store.ts';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  useEditProfileMutation,
  useLogoutMutation,
  useOwnProfileQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { clearTokens } from '@store';
import { useQueryClient } from '@tanstack/react-query';
import { paths } from '@types';

const getProfileAvatarTooltipText = (
  profile: paths[`/profile`]['get']['responses']['200']['content']['application/json'],
) => {
  if (!profile || !profile.assignments) {
    return '';
  }
  const roles =
    profile.assignments.length > 1
      ? `${profile.assignments[0].role} +${profile.assignments.length - 1}`
      : profile.assignments[0].role;
  const title = `${profile.first_name} ${profile.last_name} | ${roles}`;

  return title;
};

export const useProfileMenu = () => {
  const { user } = useUserStore();
  const profile = useOwnProfileQuery();
  const editProfileMutation = useEditProfileMutation();
  const logoutMutation = useLogoutMutation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isSignoutConfirmationOpen, setSignoutConfirmationOpen] =
    useState(false);
  const handleSignoutConfirmationOpen = () => {
    setSignoutConfirmationOpen(true);
  };
  const handleSignoutConfirmationClose = () => {
    setSignoutConfirmationOpen(false);
  };
  const confirmSignout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      clearTokens();
      localStorage.clear();
      navigate('/auth/login');
      queryClient.removeQueries();
    }
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isAwayModalOpen, setIsAwayModalOpen] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAwayModalClose = () => {
    setIsAwayModalOpen(false);
  };

  const handleChangeStatusToAway = async () => {
    handleClose();
    setIsAwayModalOpen(true);
    try {
      await editProfileMutation.mutateAsync({
        activity_status: 'away',
      });
      toast.success('Status changed.');
    } catch (error) {
      toast.error(formatApiError(error));
      setIsAwayModalOpen(false);
    }
  };

  const handleChangeStatusToActive = async () => {
    handleClose();
    try {
      await editProfileMutation.mutateAsync({
        activity_status: 'active',
      });
      toast.success('Status changed.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsAwayModalOpen(false);
    }
  };

  // useEffect(() => {
  //   if (profile.data && profile?.data?.activity_status === 'away') {
  //     setIsAwayModalOpen(true);
  //   }
  // }, [profile]);

  return {
    user,
    navigate,
    anchorEl,
    handleOpen,
    handleClose,
    profile: { ...profile.data!, id: user?.id || '' },
    handleChangeStatusToAway,
    isAwayModalOpen,
    handleAwayModalClose,
    handleChangeStatusToActive,
    handleSignoutConfirmationOpen,
    handleSignoutConfirmationClose,
    isSignoutConfirmationOpen,
    confirmSignout,
    tooltipText: getProfileAvatarTooltipText(profile.data!),
  };
};
